import { useMemo, useState } from 'react';
import './trackedTimeTable.css';
import { UserColor } from '../../utils/UserColor';
import type { IFilter } from '../../models/IFilter.ts';
import { adjustFontSizeAvatar } from '../../utils/UserInitialsHelper';
import { DateFieldNames } from '../../services/filterService';
import { linkService } from '../../services/linkService';
import { DateRangeMode as DateRangeModeEnum } from '../../const/DateRangeMode';
import type { IEmployeeTrackedTime } from '../../models/IEmployeeTrackedTime';
import { formatHours } from '../../services/commonFunctions';
import { findLast } from '../../utils/ArrayHelper';
import { getWeekEnd } from '../../utils/dateUtils';
import { openEmployeeRelationStatsPage } from '../../utils/clientUtils';
import '../../styles/theme.css';
import { Skeleton } from '@mui/material';
import type { DetailRoute } from '../../api/summaryApi';
type Props = {
  Data: IEmployeeTrackedTime[];
  Dates: Date[];
  Filters: IFilter[];
  WorkLogLink: string;
  ClientRelationStatsRoute?: DetailRoute;
  DateRangeMode?: DateRangeModeEnum;
  ParentBcId: number;
  isLoading: boolean;
  showPrices?: boolean;
  currencySymbol?: string;
  /** Official dashboard total (matches Totals card); avoids 1¢ drift from summing per-row TotalPrice. */
  dashboardTotalPrice?: number;
};

const TrackedTimeTable = ({
  Data,
  Dates,
  Filters,
  WorkLogLink,
  ClientRelationStatsRoute,
  DateRangeMode,
  ParentBcId,
  isLoading,
  showPrices = false,
  currencySymbol = '',
  dashboardTotalPrice,
}: Props) => {
  const [expanedContractors, setExpandedContractors] = useState<number[]>([]);
  const toDateKey = (value: Date) => new Date(value).toISOString().slice(0, 10);

  const trackedTimeByEmployee = useMemo(() => {
    return new Map(
      Data.map((entry) => [
        entry.Id,
        new Map(entry.TrackedTime.map((item) => [toDateKey(item.Date), item])),
      ])
    );
  }, [Data]);

  const openClientRelationForTotal = (employee: number) => {
    if (DateRangeMode === DateRangeModeEnum.Week) {
      openSelected(employee);
    } else {
      const trackedTimeForEmployee = trackedTimeByEmployee.get(employee);

      if (!trackedTimeForEmployee) return;

      const dataFilled = findLast(
        Array.from(trackedTimeForEmployee.values()),
        (item) => item.TotalHours != null && item.TotalHours > 0
      );
      if (!dataFilled?.Date) {
        const trackedTimeValues = Array.from(trackedTimeForEmployee.values());
        const lastTrackedTime = trackedTimeValues[trackedTimeValues.length - 1];
        if (!lastTrackedTime) return;
        openSelected(employee, new Date(lastTrackedTime.Date));
        return;
      }

      openSelected(employee, new Date(dataFilled.Date));
    }
  };

  const openSelected = (employee: number, date?: Date) => {
    if (!DateRangeMode) {
      return;
    }

    openEmployeeRelationStatsPage({
      employee,
      dateFilters: Filters,
      dateRangeMode: DateRangeMode,
      clientRelationStatsRoute: ClientRelationStatsRoute,
      bcId: ParentBcId,
      date,
    });
  };

  const openClientRelationStatsPage = (employee: number) => {
    openSelected(employee);
  };

  const openSelectedForTotal = () => {
    const filters = Filters.filter((f) => f.DataField !== DateFieldNames.Employee);
    linkService.openWorkLogWithFilters(filters, WorkLogLink);
  };

  const formatDate = (startDate: Date) => {
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    if (DateRangeMode === DateRangeModeEnum.Week) {
      return new Intl.DateTimeFormat('en-US', options).format(startDate);
    }
    const endDate = getWeekEnd(startDate);
    const formattedStart = new Intl.DateTimeFormat('en-US', options).format(startDate);
    const formattedEnd = new Intl.DateTimeFormat('en-US', options).format(endDate);
    return `${formattedStart} – ${formattedEnd}`;
  };

  const totalPrice = dashboardTotalPrice ?? 0;

  return (
    <div className={`${DateRangeMode?.toLowerCase() ?? ''} hourly-container`}>
      <h2 className="hourly-title">Hourly</h2>
      <div className="hourly-card">
        {!isLoading && Data.length > 0 ? (
          <div className="hourly-table">
            <div className="table-hourly-header">
              <div className="contract-header" style={{ textAlign: 'left' }}>
                Contract
              </div>
              <div className="dates-header-info">
                <div className="date-header-container">
                  {Dates.map((date, key) => (
                    <div key={key} className="date-header">
                      {formatDate(date)}
                    </div>
                  ))}
                </div>
                <div className="total-in-row">
                  <div className="total-in-row-columns">
                    <div className="hours-header">Hours</div>
                    {showPrices && <div className="hours-header">Price</div>}
                  </div>
                  <div className="hours-header-desktop">Hours</div>
                  {showPrices && <div className="hours-header-desktop">Price</div>}
                  <div className="more-info"></div>
                </div>
              </div>
            </div>
            <div>
              {Data.map((entry, index) => {
                const colorScheme = UserColor.getColor(entry.Name);
                return (
                  <div key={index} className="hourly-row">
                    <div className="header-contractors">
                      <div
                        onClick={() => openClientRelationStatsPage(entry.Id)}
                        className="contract-cell contract-header contract-name-cell"
                      >
                        <span className="d-inline-block" data-toggle="tooltip" title={entry.Name}>
                          <div
                            className="kanban-card__avatar"
                            style={{
                              backgroundColor: colorScheme.backgroundColor,
                              fontSize: adjustFontSizeAvatar(entry.Initials.length),
                            }}
                          >
                            {entry.Initials}
                          </div>
                        </span>
                        <div className="name-container">
                          <span className="name">{entry.Name}</span>
                        </div>
                      </div>
                      <div
                        className={`${expanedContractors.some((id) => id == entry.Id) ? 'not-visible-total-row-mobile' : 'visible-total-row-mobile'} total-data-row-container-mobile`}
                      >
                        <div className="total-data-row">
                          <div onClick={() => openSelectedForTotal()} className="hours-cell">
                            {formatHours(entry.TotalTrackedTime)}
                          </div>
                        </div>
                        {showPrices && (
                          <div className="total-data-row">
                            <div onClick={() => openSelectedForTotal()} className="hours-cell">
                              {`${currencySymbol}${(Number.isFinite(entry.TotalPrice) ? entry.TotalPrice : 0).toFixed(2)}`}
                            </div>
                          </div>
                        )}
                      </div>
                      <div
                        className="more-info"
                        onClick={() => {
                          setExpandedContractors((prev) =>
                            prev.includes(entry.Id)
                              ? prev.filter((id) => id !== entry.Id)
                              : [...prev, entry.Id]
                          );
                        }}
                      >
                        {!expanedContractors.some((id) => id == entry.Id) ? (
                          <span>▼</span>
                        ) : (
                          <span>▲</span>
                        )}
                      </div>
                    </div>
                    <div
                      className={`data-section ${expanedContractors.some((id) => id == entry.Id) ? 'expanded-for-mobile' : 'not-expaned-for-mobile'}`}
                    >
                      <div>
                        <div className="dates-mobile">
                          <div className="date-header-container">
                            {Dates.map((date, key) => (
                              <div key={key} className="date-header">
                                {formatDate(date)}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="dates-data">
                        <div className="date-header-container">
                          {Dates.map((date) => {
                            const trackedTime = trackedTimeByEmployee
                              .get(entry.Id)
                              ?.get(toDateKey(date));

                            return (
                              <div
                                key={toDateKey(date)}
                                onClick={() =>
                                  trackedTime && openSelected(entry.Id, new Date(trackedTime.Date))
                                }
                                className="hours-cell date-cell"
                              >
                                <div className="date-cell-value">
                                  {trackedTime?.TotalHours == null
                                    ? '-'
                                    : formatHours(trackedTime.TotalHours)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="total-in-row">
                          <div
                            onClick={() => openClientRelationForTotal(entry.Id)}
                            className="hours-cell"
                          >
                            <div className="hours-cell-value">
                              {formatHours(entry.TotalTrackedTime)}
                            </div>
                          </div>
                          {showPrices && (
                            <div
                              onClick={() => openClientRelationForTotal(entry.Id)}
                              className="hours-cell"
                            >
                              <div className="hours-cell-value">
                                {`${currencySymbol}${(Number.isFinite(entry.TotalPrice) ? entry.TotalPrice : 0).toFixed(2)}`}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="total-row">
                <div className="total-label-main" style={{ textAlign: 'left' }}>
                  Total
                </div>
                <div className="total-data">
                  <div className="total-data-row-empty-container">
                    {Dates.map((date) => (
                      <div className="empty-cell-total" key={toDateKey(date)}></div>
                    ))}
                  </div>
                  <div className="total-data-row-container">
                    <div className="total-data-row">
                      <div className="total-label">Total Tracked Time</div>
                      <div onClick={() => openSelectedForTotal()} className="hours-cell">
                        {formatHours(Data.reduce((a, b) => a + b.TotalTrackedTime, 0))}
                      </div>
                    </div>
                    {showPrices && (
                      <div className="total-data-row">
                        <div className="total-label">Total Price</div>
                        <div onClick={() => openSelectedForTotal()} className="hours-cell">
                          {`${currencySymbol}${(Number.isFinite(totalPrice) ? totalPrice : 0).toFixed(2)}`}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : isLoading ? (
          <div className="skeleton">
            <div className="skeleton-full-item">
              <Skeleton variant="text" width={220} sx={{ margin: '0 5px' }} />
              <Skeleton variant="text" sx={{ flexGrow: 1 }} />
            </div>
          </div>
        ) : (
          <p className="no-data">
            There is no data for the selected{' '}
            {DateRangeMode === DateRangeModeEnum.Week ? 'week' : 'month'}
          </p>
        )}
      </div>
    </div>
  );
};

export default TrackedTimeTable;
